import * as React from 'react'
import { ReactElement } from 'react'
import {
    CheckboxVisibility,
    DetailsList,
    DetailsListLayoutMode,
    DetailsRow,
    IColumn,
    Icon,
    MessageBar,
    MessageBarType,
    SelectionMode,
    Stack,
} from '@fluentui/react'
import Loading from '../../../components/Loading'
import { isKvkError, KvkErrorResponse } from '../../Dialog/ChamberOfCommerce/KvkSearchDialog'
import { AxiosError } from 'axios'
import { DataPromise } from '../../../context/AxiosContext'
import { useTranslation } from 'react-i18next'

export type KvkSearchResult = {
    number: string
    name: string
    street: string
    locality: string
    branchNumber?: string
    type: string
}

const typeIcons: Record<string, string> = {
    rechtspersoon: 'Bank',
    hoofdvestiging: 'Home',
    nevenvestiging: 'MapPin',
}

type Props = {
    items: DataPromise<KvkSearchResult[], undefined, KvkErrorResponse>
    onSelect: (item: KvkSearchResult) => void
}

const getColumns = (translate: (key: string) => string): IColumn[] => [
    {
        key: 'type',
        name: '',
        minWidth: 20,
        maxWidth: 20,
        onRender: (item: KvkSearchResult) =>
            typeIcons[item.type] && (
                <Icon iconName={typeIcons[item.type]} title={translate(`party.edit.coc.type.${item.type}`)} aria-label={translate(`party.edit.coc.type.${item.type}`)} />
            ),
    },
    {
        key: 'number',
        fieldName: 'number',
        name: translate('party.edit.coc.number'),
        minWidth: 60,
        maxWidth: 60,
        isCollapsible: true,
    },
    {
        key: 'branchNumber',
        fieldName: 'branchNumber',
        name: translate('party.edit.coc.branchNumber'),
        minWidth: 90,
        maxWidth: 110,
        isCollapsible: true,
    },
    {
        key: 'name',
        fieldName: 'name',
        name: translate('party.edit.coc.name'),
        minWidth: 200,
        isResizable: true,
    },
    {
        key: 'street',
        fieldName: 'street',
        name: translate('party.edit.coc.street'),
        minWidth: 160,
        isResizable: true,
        isCollapsible: true,
    },
    {
        key: 'locality',
        fieldName: 'locality',
        name: translate('party.edit.coc.locality'),
        minWidth: 120,
        isResizable: true,
    },
]

const parseError = (translate: (key: string) => string, error: AxiosError<KvkErrorResponse>): string =>
    isKvkError(error)
        ? error.response.data.fout.map(e => translate(`party.edit.coc.search.error.${e.code.toLowerCase()}`) ?? e.omschrijving).join(', ')
        : translate('party.edit.coc.search.error.unknown')

export default function KvkSearchResultList(props: Props): ReactElement {
    const { t: translate } = useTranslation()

    if (props.items.loading) {
        return <Loading />
    }

    if (props.items.rejected) {
        return <MessageBar messageBarType={MessageBarType.error}>{parseError(translate, props.items?.error)}</MessageBar>
    }

    if (!props.items.loaded) {
        return <MessageBar messageBarType={MessageBarType.info}>{translate('party.edit.coc.search.description')}</MessageBar>
    }

    return (
        <DetailsList
            columns={getColumns(translate)}
            onItemInvoked={item => props.onSelect(item)}
            items={props.items.data}
            onRenderRow={rowProps => (
                <Stack onClick={() => props.onSelect(rowProps.item)}>
                    <DetailsRow {...rowProps} styles={{ root: { cursor: 'pointer' } }} />
                </Stack>
            )}
            compact
            checkboxVisibility={CheckboxVisibility.hidden}
            selectionMode={SelectionMode.single}
            layoutMode={DetailsListLayoutMode.justified}
        />
    )
}
